import React from 'react';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import { Users, CreditCard, TrendingUp, Loader2 } from 'lucide-react';
import { useDashboardStats } from '../../hooks/useDashboardStats'; // Assuming hook provides translated error

// Interface for MonthlyIncome if needed for clarity, otherwise rely on hook's type
// interface MonthlyIncome { [currency: string]: number; }

const DashboardStats: React.FC = () => {
  const { t } = useTranslation(); // Get the t function
  const {
    activePatientCount,
    monthlyIncome,
    predictedMonthlyIncome,
    isLoading,
    error,
  } = useDashboardStats();

  // Helper function to format currency, potentially localizable later
  const formatCurrency = (
    amount: number,
    currency: string,
    locale?: string
  ) => {
    // Basic formatting, respecting HUF rounding
    const value = currency === 'HUF' ? Math.round(amount) : amount;
    // Use locale-sensitive formatting
    return value.toLocaleString(locale, {
      // Pass locale if available (e.g., from i18n.language)
      minimumFractionDigits: currency === 'HUF' ? 0 : 2,
      maximumFractionDigits: currency === 'HUF' ? 0 : 2,
    });
  };

  // Define card data structure for easier mapping
  const statsCards = [
    {
      titleKey: 'dashboardStats.activePatients',
      icon: Users,
      iconBgColor: 'bg-indigo-50',
      iconTextColor: 'text-indigo-600',
      getValue: () => activePatientCount,
      formatValue: (value: number) => value.toLocaleString(), // Basic number format
    },
    {
      titleKey: 'dashboardStats.incomeThisMonth',
      icon: CreditCard,
      iconBgColor: 'bg-emerald-50',
      iconTextColor: 'text-emerald-600',
      getValue: () => monthlyIncome,
      formatValue: (income: { [key: string]: number }) => income, // Pass object to render logic
    },
    {
      titleKey: 'dashboardStats.predictedIncome',
      icon: TrendingUp,
      iconBgColor: 'bg-blue-50',
      iconTextColor: 'text-blue-600',
      getValue: () => predictedMonthlyIncome,
      formatValue: (income: { [key: string]: number }) => income, // Pass object to render logic
    },
  ];

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {statsCards.map((card, index) => (
        <div
          key={index}
          className="bg-white p-6 rounded-lg shadow-sm border border-gray-200"
        >
          {
            isLoading ? (
              // Loading State
              <div className="flex items-center justify-center h-full min-h-[80px]">
                {' '}
                {/* Added min-height */}
                <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                {/* Translate loading text */}
                <span className="ml-2 text-sm text-gray-500">
                  {t('dashboardStats.loading')}
                </span>
              </div>
            ) : error && index === 0 ? ( // Show error only once, e.g., in the first card
              // Error State
              <div className="text-red-600 text-sm min-h-[80px] flex items-center">
                {' '}
                {/* Adjusted color & min-height */}
                {error} {/* Display translated error from hook */}
              </div>
            ) : !error ? (
              // Data Display State
              <div className="flex items-start space-x-4">
                <div className={`p-2 ${card.iconBgColor} rounded-lg`}>
                  <card.icon className={`w-6 h-6 ${card.iconTextColor}`} />
                </div>
                <div>
                  {/* Render value based on type */}
                  {typeof card.getValue() === 'number' ? (
                    // Render simple number value
                    <div className="text-2xl font-semibold text-gray-900">
                      {card.formatValue(card.getValue() as number)}
                    </div>
                  ) : (
                    // Render income object value
                    <div className="space-y-1">
                      {Object.keys(card.getValue() as object).length === 0 ? (
                        <div className="text-2xl font-semibold text-gray-900">
                          0
                        </div>
                      ) : (
                        Object.entries(
                          card.getValue() as { [key: string]: number }
                        ).map(([currency, amount]) => (
                          <div
                            key={currency}
                            className="text-2xl font-semibold text-gray-900"
                          >
                            {/* Use formatting helper */}
                            {`${formatCurrency(amount, currency)} ${currency}`}
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  {/* Translate card title */}
                  <div className="text-sm font-medium text-gray-500 mt-1">
                    {t(card.titleKey)}
                  </div>
                </div>
              </div>
            ) : null /* Don't render subsequent cards if there was an error */
          }
        </div>
      ))}
    </div>
  );
};

// Exporting both default and named for flexibility
export default DashboardStats;
export { DashboardStats };
